import { contractEndsMsg, durationOfContractMsg, extensionMsg, oneTimePaymentMsg, t } from '../../common/i18n';
import { formatTimestampToUTCDDMMYYYY } from '../../common/utils/dateUtils';
import { getPriceAsText } from '../../common/utils/priceUtils';
import { capitalize } from '../../common/utils/stringUtils';
import { ChangeRequestDetailsAtomicChanges } from '../../generated/api/changeRequestDetailsAtomicChanges';
import { CustomerOrderProductType } from '../../generated/api/customerOrderProductType';
import { Icon } from '../Icon/Icon';
import NameEnum = ChangeRequestDetailsAtomicChanges.NameEnum;
import type { CLDescriptionItem } from '../../common/types/componentLibrary';
import type { ChangeRequestDetails } from '../../generated/api/changeRequestDetails';
import type { ReactNode } from 'react';

import './changeRequestUtils.scss';

export const NONE_TEXT = '-';

export const isChangeRequestId = (id: string) => id.startsWith('CR-');

const HighlightedNewValue = ({ description }: { description: ReactNode }) => (
  <div className="change-request-utils-highlight-new">
    {description}
    <Icon color="green-600" icon="check" size="m" />
  </div>
);

const HighlightedRemovedValue = ({ description }: { description: ReactNode }) => (
  <div className="change-request-utils-highlight-old">
    {description}
    <Icon color="red-600" icon="block" size="s" />
  </div>
);

const UserIdentity = ({ value, emptyFallback = NONE_TEXT }: { value?: string; emptyFallback?: string }) => {
  if (!value || value === NONE_TEXT) {
    return <>{emptyFallback}</>;
  }
  // Example value: John Doe (john.doe@example.com)
  const match = value.match(/^([^()]+)\s*\(([^)]+)\)$/);
  if (match) {
    const name = match[1].trim();
    const email = match[2].trim();
    return (
      <div>
        <div>{name}</div>
        <div>{email}</div>
      </div>
    );
  }
  return <div>{value}</div>;
};

const AddedAddons = ({ addons }: { addons: string[] }) => (
  <>
    {addons.map((addon, idx) => (
      <div key={addon + idx}>
        <HighlightedNewValue description={addon} />
      </div>
    ))}
  </>
);

const RemovedAddons = ({ addons }: { addons: string[] }) => (
  <>
    {addons.map((addon, idx) => (
      <div key={addon + idx}>
        <HighlightedRemovedValue description={addon} />
      </div>
    ))}
  </>
);

const OneTimePriceDescription = ({ priceText }: { priceText: string }) => (
  <>
    {priceText}
    <br />
    {t.TJOE('One-time fee will be added to the next invoice.')}
  </>
);

const getEppExtensionPeriodEndDescription = (endDateTimestamp: number): string | undefined => {
  if (!endDateTimestamp) {
    return undefined;
  }
  if (!Number.isFinite(endDateTimestamp) || endDateTimestamp <= 0) {
    return undefined;
  }
  const endDate = new Date(endDateTimestamp);
  endDate.setMonth(endDate.getMonth() + 6);
  const updatedTimestamp = endDate.getTime();

  return `${formatTimestampToUTCDDMMYYYY(endDateTimestamp)} | ${t.M41V(extensionMsg)} ${formatTimestampToUTCDDMMYYYY(updatedTimestamp)}`;
};

const addFieldIfValue = (
  fields: CLDescriptionItem[],
  value: string | undefined,
  title: string,
  description?: ReactNode
) => {
  if (value) {
    fields.push({ title, description: description ?? value });
  }
};

const addHighlightedFieldIfValue = (
  fields: CLDescriptionItem[],
  value: string | undefined,
  title: string,
  description?: ReactNode
) => {
  if (value) {
    fields.push({
      title,
      description: <HighlightedNewValue description={description ?? value} />,
    });
  }
};

export const getChangeRequestChangedFields = (changeRequest: ChangeRequestDetails): CLDescriptionItem[] => {
  const fields: CLDescriptionItem[] = [];
  const atomicChanges = changeRequest.atomicChanges ?? [];
  atomicChanges.forEach(change => {
    switch (change.name) {
      case NameEnum.SUBSCRIPTION_TYPE_CHANGE: {
        addFieldIfValue(fields, change.oldValue, t.SK41('Old subscription type'));
        addHighlightedFieldIfValue(fields, change.newValue, t.XEUL('New subscription type'));
        break;
      }
      case NameEnum.BILLING_ACCOUNT_CHANGE: {
        addFieldIfValue(fields, change.oldValue, t.LHU3('Old billing agreement'));
        addHighlightedFieldIfValue(fields, change.newValue, t.MZS3('New billing agreement'));
        break;
      }
      case NameEnum.EPP_EXTENSION_PERIOD_CONTINUATION: {
        addFieldIfValue(
          fields,
          change.newValue,
          t.CAA9(durationOfContractMsg),
          `${change.newValue} ${t.IIBS('months + 6 months extension')}`
        );
        if (changeRequest.effectiveDate) {
          const effectiveDate = new Date(Number(changeRequest.effectiveDate));
          const monthsToAdd = Number(change.newValue);
          effectiveDate.setMonth(effectiveDate.getMonth() + monthsToAdd);
          const endDateTimestamp = effectiveDate.getTime();
          const description = getEppExtensionPeriodEndDescription(endDateTimestamp);
          if (description) {
            fields.push({ title: t.LHJ4(contractEndsMsg), description });
          }
        }
        break;
      }
      case NameEnum.EPP_EXTENSION_PERIOD_END: {
        const description = getEppExtensionPeriodEndDescription(Number(change.newValue));
        addFieldIfValue(fields, description, t.LHJ4(contractEndsMsg), description);
        break;
      }
      case NameEnum.USER_CHANGE: {
        addFieldIfValue(fields, change.oldValue, t.FVC8('Old user'), <UserIdentity value={change.oldValue} />);
        addHighlightedFieldIfValue(
          fields,
          change.newValue,
          t.ON49('New user'),
          <UserIdentity value={change.newValue} />
        );
        break;
      }
      case NameEnum.PURPOSE_OF_USE_CHANGE: {
        addFieldIfValue(
          fields,
          change.oldValue,
          t.XYAM('Old purpose of use'),
          <UserIdentity value={change.oldValue} />
        );
        addHighlightedFieldIfValue(fields, change.newValue, t.KZ77('New purpose of use'));
        break;
      }
      case NameEnum.DEVICE_CHANGE: {
        addFieldIfValue(fields, change.oldValue, t.JN29('Old device'));
        addHighlightedFieldIfValue(fields, change.newValue, t.TLCA('New device'));
        break;
      }
      case NameEnum.SUBSCRIPTION_TERMINATION: {
        const formattedDate = change.newValue ? formatTimestampToUTCDDMMYYYY(Number(change.newValue) || 0) : undefined;
        addFieldIfValue(fields, formattedDate, t.T0HO('Termination time'));
        break;
      }
      case NameEnum.PHONE_NUMBER_CHANGE: {
        addFieldIfValue(fields, change.oldValue, t.BGVG('Old phone number'));
        break;
      }
      case NameEnum.INSTALLATION_ADDRESS_CHANGE: {
        addFieldIfValue(fields, change.oldValue, t.NIDB('Old installation address'));
        addFieldIfValue(fields, change.newValue, t.VBUC('New installation address'));
        break;
      }
      case NameEnum.SIM_CARD_ORDER: {
        addFieldIfValue(fields, change.oldValue, t.JGSK('Old SIM card number'));
        addFieldIfValue(fields, change.newValue, t.H9EI('New SIM card number'));
        break;
      }
      default:
        return;
    }
  });
  const addedAddons = atomicChanges
    .filter(c => c.name === NameEnum.ADDON_ADDITION && c.newValue)
    .map(c => c.newValue as string);
  if (addedAddons.length) {
    fields.push({ title: t.QRED('Added services'), description: <AddedAddons addons={addedAddons} /> });
  }
  const removedAddons = atomicChanges
    .filter(c => c.name === NameEnum.ADDON_REMOVAL && c.newValue)
    .map(c => c.newValue as string);
  if (removedAddons.length) {
    fields.push({
      title: t.U88X('Removed services'),
      description: <RemovedAddons addons={removedAddons} />,
    });
  }
  return fields;
};

export const getChangeRequestPriceFields = (changeRequest: ChangeRequestDetails): CLDescriptionItem[] => {
  const { rcNew, rcOld, ocNew, productType } = changeRequest;
  const priceFields: CLDescriptionItem[] = [];
  const subscriptionTypeMsg =
    productType === CustomerOrderProductType.DEVICE || productType === CustomerOrderProductType.EPP
      ? t.ACH1('device')
      : t.D2UO('subscription');
  if (rcOld !== undefined) {
    priceFields.push({
      title: capitalize(t.TWB3('The price of the {} before the change', subscriptionTypeMsg)),
      description: getPriceAsText(rcOld, undefined, true),
    });
  }
  if (rcNew !== undefined) {
    const highlight = rcNew !== rcOld;
    const priceText = getPriceAsText(rcNew, undefined, true);
    priceFields.push({
      title: capitalize(t.AHN6('The price of the {} after the change', subscriptionTypeMsg)),
      description: highlight ? <HighlightedNewValue description={priceText} /> : priceText,
    });
  }
  if (ocNew !== undefined) {
    const priceText = getPriceAsText(ocNew, undefined, false) ?? '0';
    priceFields.push({
      title:
        productType === CustomerOrderProductType.DEVICE ? t.ML58('Device one-time payment') : t.ASEI(oneTimePaymentMsg),
      description: <OneTimePriceDescription priceText={priceText} />,
    });
  }
  return priceFields;
};
